import React, { useEffect, useState } from "react";
import { HiOutlineArrowTopRightOnSquare } from "react-icons/hi2";
import { supportApi } from "../../api/support";
import { ISupportContextResponse } from "../../data-access";
import { Spinner } from "../../ui";
import { EMPTY_VALUE, formatDate, formatDateTime, getDisplayName } from "./format";

interface IContextCardProps {
  conversationId: string;
}

const baseClass = "support-context";

const textOrEmpty = (value: string | null): string => value || EMPTY_VALUE;

export const ContextCard = ({ conversationId }: IContextCardProps): JSX.Element => {
  const [context, setContext] = useState<ISupportContextResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasFailed, setHasFailed] = useState<boolean>(false);

  useEffect(() => {
    let isActive = true;

    setIsLoading(true);
    setHasFailed(false);

    supportApi
      .getContext(conversationId)
      .then((response) => {
        if (isActive) {
          setContext(response);
        }
      })
      .catch(() => {
        if (isActive) {
          setContext(null);
          setHasFailed(true);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [conversationId]);

  if (isLoading) {
    return (
      <div className={baseClass}>
        <Spinner />
      </div>
    );
  }

  if (hasFailed || !context) {
    return (
      <div className={baseClass}>
        <div className={`${baseClass}__empty`}>Context is not available for this user</div>
      </div>
    );
  }

  const { user, billing, activity_30d: activity } = context;

  // A user without a team comes back with nulls and zeroes across billing,
  // so every row below renders a placeholder rather than being hidden.
  const attribution = user.signup_attribution
    ? Object.keys(user.signup_attribution)
        .map((key) => `${key}: ${user.signup_attribution?.[key]}`)
        .join(", ")
    : "";

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__section`}>
        <div className={`${baseClass}__title`}>User</div>
        <div className={`${baseClass}__row`}>
          <span>Name</span>
          <span>{getDisplayName(user.full_name, user.email)}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Email</span>
          <span>{user.email}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Role</span>
          <span>{textOrEmpty(user.role)}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Signed up</span>
          <span>{formatDate(user.created_at)}</span>
        </div>
        {attribution && (
          <div className={`${baseClass}__row`}>
            <span>Source</span>
            <span>{attribution}</span>
          </div>
        )}
      </div>

      <div className={`${baseClass}__section`}>
        <div className={`${baseClass}__title`}>Billing</div>
        <div className={`${baseClass}__row`}>
          <span>Plan</span>
          <span>{textOrEmpty(billing.plan)}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Status</span>
          <span>{textOrEmpty(billing.billing_status)}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Trial</span>
          <span>
            {formatDate(billing.trial_date_start)} to {formatDate(billing.trial_date_end)}
          </span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Plan period</span>
          <span>
            {formatDate(billing.plan_start_date)} to {formatDate(billing.plan_expire_date)}
          </span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Credits</span>
          <span>
            {billing.ai_credits_used} of {billing.ai_credits_total}
          </span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Seats</span>
          <span>
            {billing.seats_used} of {billing.seats_total}
          </span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Promocode</span>
          <span>{textOrEmpty(billing.promocode)}</span>
        </div>
        {billing.stripe_url && (
          <a
            className={`${baseClass}__link`}
            href={billing.stripe_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Stripe
            <HiOutlineArrowTopRightOnSquare size={14} />
          </a>
        )}
      </div>

      <div className={`${baseClass}__section`}>
        <div className={`${baseClass}__title`}>Activity, last 30 days</div>
        <div className={`${baseClass}__row`}>
          <span>Job analyses</span>
          <span>{activity.job_analyses}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>AI proposals</span>
          <span>{activity.ai_proposals}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>AI answers</span>
          <span>{activity.ai_answers}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Auto-bid jobs</span>
          <span>{activity.auto_bid_jobs}</span>
        </div>
        <div className={`${baseClass}__row`}>
          <span>Last seen</span>
          <span>{formatDateTime(activity.last_seen_at)}</span>
        </div>
      </div>
    </div>
  );
};
